/**
 * FASE 5 / BLOCO BA-4 — camada de acesso à API do BI.
 *
 * ┌───────────────────────────────────────────────────────────────────────────────────┐
 * │  EXCEÇÃO ARQUITETURAL APROVADA — NÃO DISSOLVER ESTE MÓDULO NOS COMPONENTES.        │
 * │                                                                                   │
 * │  O `CLAUDE.md` do `web/` determina "não criar camada de serviço — `$axios` direto  │
 * │  no componente". Este arquivo é uma exceção **deliberada e aprovada** (FASE 5,     │
 * │  BA-4), por um motivo de segurança:                                                │
 * │                                                                                   │
 * │  a proibição de enviar campos capazes de influenciar o ESCOPO INSTITUCIONAL        │
 * │  (`CAMPOS_PROIBIDOS`) precisa existir em UM lugar só e ser testável. Distribuída   │
 * │  pelos componentes, bastaria um esquecimento em uma tela nova para o frontend      │
 * │  voltar a poder mandar `instituicoes_permitidas` e ampliar o próprio acesso.       │
 * │                                                                                   │
 * │  Quem refatorar isto: mover as chamadas para dentro dos componentes REINTRODUZ     │
 * │  essa classe de falha. Se for necessário, mova junto o gate que a impede.          │
 * │                                                                                   │
 * │  ESCOPO DESTA DEFESA: é defesa ADICIONAL, jamais controle de autorização primário. │
 * │  A autoridade real é o backend (`BiAcessoService::instituicoesVisiveis`, coberto   │
 * │  por teste de escalada de privilégio e mutação). Um cliente que ignore este        │
 * │  arquivo — curl, Postman, outro app — continua sendo barrado lá. Este módulo       │
 * │  apenas impede que o NOSSO frontend seja o vetor.                                  │
 * └───────────────────────────────────────────────────────────────────────────────────┘
 *
 * REGRA ESTRUTURAL DESTE MÓDULO: aqui NÃO existe regra de negócio.
 *
 * Autorização, escopo institucional, cálculo de métrica, agregação e validação são
 * soberanos no backend, que já foi auditado nos blocos BA-1/2/3. Este arquivo só monta
 * URL, envia o que a tela tem e devolve o que o servidor respondeu.
 *
 * Em particular, `instituicoes_permitidas` NUNCA é enviado daqui. O backend o deriva do
 * usuário autenticado (`BiAcessoService::instituicoesVisiveis`). Se o front pudesse mandá-lo,
 * bastaria o DevTools para ampliar o próprio acesso — é o primeiro ataque da auditoria de
 * frontend deste bloco.
 */

/** Campos que o front está proibido de enviar: são decididos no servidor. */
const CAMPOS_PROIBIDOS = ['instituicoes_permitidas', 'user_id', 'tipo_usuario_efetivo']

function limpar (filtros) {
  const saida = {}
  Object.keys(filtros || {}).forEach(function (k) {
    if (CAMPOS_PROIBIDOS.indexOf(k) !== -1) return
    const v = filtros[k]
    if (v === null || v === undefined || v === '') return
    saida[k] = v
  })
  return saida
}

export default {
  /** Injetado pelo componente: { axios, base, auth } */
  criar (ctx) {
    const base = ctx.base
    const auth = ctx.auth
    const http = ctx.axios

    return {
      // ---------------------------------------------------------------- painéis
      listarPaineis () {
        return http.get(base + '/api/bi-painel/listar', auth)
      },
      dadosPainel (slug, filtros) {
        return http.post(base + '/api/bi-painel/dados/' + encodeURIComponent(slug), limpar(filtros), auth)
      },
      recorte (slug, dimensao, filtros) {
        return http.post(
          base + '/api/bi-painel/recorte/' + encodeURIComponent(slug) + '/' + encodeURIComponent(dimensao),
          limpar(filtros), auth
        )
      },

      // ------------------------------------------------------------------ carga
      executarCarga (dominio, dataReferencia) {
        return http.post(base + '/api/bi-carga/executar', { dominio: dominio, data_referencia: dataReferencia }, auth)
      },
      uploadCarga (formData) {
        const cfg = Object.assign({}, auth)
        cfg.headers = Object.assign({}, cfg.headers, { 'Content-Type': 'multipart/form-data' })
        return http.post(base + '/api/bi-carga/upload', formData, cfg)
      },
      cargaHttp (url, dataReferencia) {
        return http.post(base + '/api/bi-carga/http', { url: url, data_referencia: dataReferencia }, auth)
      },
      historicoCargas (params) {
        return http.get(base + '/api/bi-carga/historico', Object.assign({ params: limpar(params) }, auth))
      },
      errosCarga (id) {
        return http.get(base + '/api/bi-carga/erros/' + encodeURIComponent(id), auth)
      },

      // --------------------------------------------------------------- linhagem
      linhagem (dominio) {
        return http.get(base + '/api/bi-linhagem/porDominio', Object.assign({ params: limpar({ dominio: dominio }) }, auth))
      },
      resumoLinhagem () {
        return http.get(base + '/api/bi-linhagem/resumo', auth)
      },

      // -------------------------------------------------------------------- geo
      mapa (filtros) {
        return http.post(base + '/api/bi-geo/mapa', limpar(filtros), auth)
      },
      providersDistancia () {
        return http.get(base + '/api/bi-geo/providers', auth)
      },

      // ---------------------------------------------------------------- métricas
      listarMetricas (params) {
        return http.get(base + '/api/bi-metrica/listar', Object.assign({ params: limpar(params) }, auth))
      },
      detalheMetrica (chave) {
        return http.get(base + '/api/bi-metrica/detalhe/' + encodeURIComponent(chave), auth)
      },
      versoesMetrica (chave) {
        return http.get(base + '/api/bi-metrica/versoes/' + encodeURIComponent(chave), auth)
      },
      validarMetrica (payload) {
        return http.post(base + '/api/bi-metrica/validar', payload, auth)
      },
      salvarMetrica (payload) {
        return http.post(base + '/api/bi-metrica/salvar', payload, auth)
      },
      avaliarMetrica (chave, filtros) {
        return http.post(base + '/api/bi-metrica/avaliar/' + encodeURIComponent(chave), limpar(filtros), auth)
      },
      removerMetrica (chave) {
        return http.post(base + '/api/bi-metrica/remover/' + encodeURIComponent(chave), {}, auth)
      },

      // ----------------------------------------------------------------- acesso
      listarAcessos (painelSlug) {
        return http.get(base + '/api/bi-acesso/listar', Object.assign({ params: limpar({ painel_slug: painelSlug }) }, auth))
      },
      conceder (slug, dados) {
        return http.post(base + '/api/bi-acesso/conceder/' + encodeURIComponent(slug), dados, auth)
      },
      revogar (id) {
        return http.post(base + '/api/bi-acesso/revogar/' + encodeURIComponent(id), {}, auth)
      },
      compartilhar (slug, filtros, horas) {
        return http.post(
          base + '/api/bi-acesso/compartilhar/' + encodeURIComponent(slug),
          Object.assign({}, limpar(filtros), { horas: horas }), auth
        )
      },
      revogarLink (token) {
        return http.post(base + '/api/bi-acesso/revogar-link', { token: token }, auth)
      },
      links () {
        return http.get(base + '/api/bi-acesso/links', auth)
      },
      alerta (metrica, operador, limiar, filtros) {
        return http.post(
          base + '/api/bi-acesso/alerta',
          Object.assign({}, limpar(filtros), { metrica: metrica, operador: operador, limiar: limiar }), auth
        )
      },

      // ---------------------------------------------------------------- export
      /**
       * O download vai por `responseType: blob` e leva EXATAMENTE os filtros que a tela
       * está exibindo — o componente passa o mesmo objeto que usou para carregar os dados.
       */
      exportar (slug, formato, filtros) {
        // `authBlob` é o getter canônico `getHeaderAuthExcel` do store (Bearer + blob).
        // O CLAUDE.md do projeto manda usar os getters em vez de montar header à mão.
        const cfg = ctx.authBlob || Object.assign({}, auth, { responseType: 'blob' })
        return http.post(
          base + '/api/bi-export/' + encodeURIComponent(slug) + '/' + encodeURIComponent(formato),
          limpar(filtros), cfg
        )
      }
    }
  },

  /** Exposto para teste: prova que os campos proibidos nunca saem daqui. */
  _limpar: limpar,
  _CAMPOS_PROIBIDOS: CAMPOS_PROIBIDOS
}
